import { Schema } from "effect";
import {
  ProjectFileActorUserIdSchema,
  ProjectFileHttpUrlSchema,
  ProjectFileIsoDateTimeSchema,
  ProjectFileNameSchema,
  ProjectFilePositiveIntegerSchema,
  ProjectFilePrivateObjectKeySchema,
  ProjectFileSha256Schema,
} from "./project-files";
import {
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
  UploadIdSchema,
} from "./sandbox";

export const ImageGenerationBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const PROJECT_IMAGE_MAX_BYTES = 12 * 1_024 * 1_024;
export const PROJECT_IMAGE_CLEANUP_BATCH_MAX = 50;
export const PROJECT_IMAGE_COST_RECONCILIATION_BATCH_MAX = 10;
export const PROJECT_IMAGE_PROVIDER_COST_RESERVATION_MICROS = 100_000;

const BoundedIdentifierSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160));
const ImagePromptSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(8_000));
const ImageTitleSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160));
const ImageAltTextSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(500));
const NonNegativeIntegerSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
export const ProjectImageVariantCounts = [1, 2, 3, 4] as const;
export const ProjectImageVariantCountSchema = Schema.Literals(ProjectImageVariantCounts);

export const ImageAspectRatios = [
  "1:1",
  "1:4",
  "1:8",
  "2:3",
  "3:2",
  "3:4",
  "4:1",
  "4:3",
  "4:5",
  "5:4",
  "8:1",
  "9:16",
  "16:9",
  "21:9",
] as const;

export const ImageAspectRatioSchema = Schema.Literals(ImageAspectRatios);

const ProjectImageRequestFields = {
  prompt: ImagePromptSchema,
  aspectRatio: ImageAspectRatioSchema,
  title: ImageTitleSchema,
  altText: ImageAltTextSchema,
} as const;

export const CreateProjectImageInputSchema = Schema.Union([
  Schema.Struct({
    ...ProjectImageRequestFields,
    operation: Schema.Literal("generate"),
    referenceArtifactRevisionIds: Schema.optionalKey(
      Schema.Array(BoundedIdentifierSchema).check(Schema.isMinLength(1), Schema.isMaxLength(14)),
    ),
  }),
  Schema.Struct({
    ...ProjectImageRequestFields,
    operation: Schema.Literal("edit"),
    referenceArtifactRevisionIds: Schema.Array(BoundedIdentifierSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(14),
    ),
  }),
]);

export const CreateProjectImagesInputSchema = Schema.Union([
  Schema.Struct({
    ...ProjectImageRequestFields,
    operation: Schema.Literal("generate"),
    requestedCount: ProjectImageVariantCountSchema,
    referenceArtifactRevisionIds: Schema.optionalKey(
      Schema.Array(BoundedIdentifierSchema).check(Schema.isMinLength(1), Schema.isMaxLength(14)),
    ),
  }),
  Schema.Struct({
    ...ProjectImageRequestFields,
    operation: Schema.Literal("edit"),
    requestedCount: ProjectImageVariantCountSchema,
    referenceArtifactRevisionIds: Schema.Array(BoundedIdentifierSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(14),
    ),
  }),
]);

export const ProjectImageGenerationCommandSchema = Schema.Struct({
  version: Schema.Literal("project-image-generation-command/v1"),
  request: CreateProjectImagesInputSchema,
});

export const ProjectImageReferenceSchema = Schema.Struct({
  version: Schema.Literal("project-image-reference/v1"),
  operationId: BoundedIdentifierSchema,
  artifactId: BoundedIdentifierSchema,
  artifactRevisionId: BoundedIdentifierSchema,
  status: Schema.Literal("ready"),
  title: ImageTitleSchema,
  altText: ImageAltTextSchema,
  aspectRatio: ImageAspectRatioSchema,
  mediaType: Schema.Literal("image/png"),
  width: Schema.Int.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(3_200)),
  height: Schema.Int.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(3_200)),
  parentRevisionId: Schema.optionalKey(BoundedIdentifierSchema),
  revision: Schema.Int.check(Schema.isGreaterThan(0)),
  createdAt: NonNegativeIntegerSchema,
});

export const ProjectImageBatchResultSchema = Schema.Struct({
  version: Schema.Literal("project-image-batch/v1"),
  operationId: BoundedIdentifierSchema,
  requestedCount: ProjectImageVariantCountSchema,
  images: Schema.NonEmptyArray(ProjectImageReferenceSchema).check(Schema.isMaxLength(4)),
}).check(
  Schema.makeFilter((result) => {
    if (result.images.length !== result.requestedCount) {
      return "a successful image batch must contain every requested variant";
    }
    if (!result.images.every((image) => image.artifactId === result.images[0].artifactId)) {
      return "image batch variants must share one artifact";
    }
    return (
      new Set(result.images.map((image) => image.artifactRevisionId)).size ===
        result.images.length || "image batch variants must have unique artifact revisions"
    );
  }),
);

export const ImageGenerationOperationStatusSchema = Schema.Literals([
  "queued",
  "generating",
  "saving",
  "ready",
  "failed",
  "cancelled",
  "outcome-unknown",
]);

export const ImageGenerationPublicErrorCodeSchema = Schema.Literals([
  "IMAGE_GENERATION_INVALID_INPUT",
  "IMAGE_GENERATION_FORBIDDEN",
  "IMAGE_GENERATION_NOT_FOUND",
  "IMAGE_GENERATION_NOT_READY",
  "IMAGE_GENERATION_PROVIDER_UNAVAILABLE",
  "IMAGE_GENERATION_NO_OUTPUT",
  "IMAGE_GENERATION_OUTPUT_INVALID",
  "IMAGE_GENERATION_STORAGE_FAILED",
  "IMAGE_GENERATION_QUOTA_EXCEEDED",
  "IMAGE_GENERATION_RATE_LIMITED",
  "IMAGE_GENERATION_CANCELLED",
  "IMAGE_GENERATION_OUTCOME_UNKNOWN",
  "IMAGE_GENERATION_UNAVAILABLE",
]);

export const ImageGenerationPublicErrorSchema = Schema.Struct({
  code: ImageGenerationPublicErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export const ImageGenerationRepositoryErrorDataSchema = Schema.Struct({
  code: Schema.Literals([
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "INVALID_INPUT",
    "NOT_FOUND",
    "STALE_ATTEMPT",
    "IMAGE_OPERATION_CONFLICT",
    "IMAGE_PROVIDER_BUDGET_DENIED",
    "QUOTA_EXCEEDED",
    "RATE_LIMITED",
    "UPLOAD_STATE_CONFLICT",
    "IMAGE_CLEANUP_CONFLICT",
  ]),
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
});

const ImageGenerationUiProjectionFields = {
  version: Schema.Literal("image-generation/v1"),
  operationId: BoundedIdentifierSchema,
  runId: BoundedIdentifierSchema,
  requestedCount: ProjectImageVariantCountSchema,
  promptSummary: ImageTitleSchema,
  aspectRatio: ImageAspectRatioSchema,
} as const;

const ProjectImageVariantsSchema = Schema.Array(ProjectImageReferenceSchema).check(
  Schema.isMaxLength(4),
);
const ProjectImageNonEmptyVariantsSchema = Schema.NonEmptyArray(ProjectImageReferenceSchema).check(
  Schema.isMaxLength(4),
);
const ImageGenerationCompletedCountSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(4),
);
const ImageGenerationFailedErrorCodeSchema = Schema.Literals([
  "IMAGE_GENERATION_INVALID_INPUT",
  "IMAGE_GENERATION_FORBIDDEN",
  "IMAGE_GENERATION_NOT_FOUND",
  "IMAGE_GENERATION_NOT_READY",
  "IMAGE_GENERATION_PROVIDER_UNAVAILABLE",
  "IMAGE_GENERATION_NO_OUTPUT",
  "IMAGE_GENERATION_OUTPUT_INVALID",
  "IMAGE_GENERATION_STORAGE_FAILED",
  "IMAGE_GENERATION_QUOTA_EXCEEDED",
  "IMAGE_GENERATION_RATE_LIMITED",
  "IMAGE_GENERATION_UNAVAILABLE",
]);

export const ImageGenerationUiProjectionSchema = Schema.Union([
  Schema.Struct({
    ...ImageGenerationUiProjectionFields,
    status: Schema.Literals(["queued", "generating", "saving"]),
    completedCount: ImageGenerationCompletedCountSchema,
    variants: ProjectImageVariantsSchema,
  }),
  Schema.Struct({
    ...ImageGenerationUiProjectionFields,
    status: Schema.Literal("ready"),
    completedCount: ImageGenerationCompletedCountSchema,
    variants: ProjectImageNonEmptyVariantsSchema,
  }),
  Schema.Struct({
    ...ImageGenerationUiProjectionFields,
    status: Schema.Literal("failed"),
    completedCount: ImageGenerationCompletedCountSchema,
    variants: ProjectImageVariantsSchema,
    errorCode: ImageGenerationFailedErrorCodeSchema,
  }),
  Schema.Struct({
    ...ImageGenerationUiProjectionFields,
    status: Schema.Literal("cancelled"),
    completedCount: ImageGenerationCompletedCountSchema,
    variants: ProjectImageVariantsSchema,
    errorCode: Schema.Literal("IMAGE_GENERATION_CANCELLED"),
  }),
  Schema.Struct({
    ...ImageGenerationUiProjectionFields,
    status: Schema.Literal("outcome-unknown"),
    completedCount: ImageGenerationCompletedCountSchema,
    variants: ProjectImageVariantsSchema,
    errorCode: Schema.Literal("IMAGE_GENERATION_OUTCOME_UNKNOWN"),
  }),
]).check(
  Schema.makeFilter((projection) => {
    if (
      projection.completedCount !== projection.variants.length ||
      projection.completedCount > projection.requestedCount
    ) {
      return "the completed count must match the bounded variant collection";
    }
    if (projection.status === "ready" && projection.completedCount !== projection.requestedCount) {
      return "a ready projection must contain every requested variant";
    }
    if (
      new Set(projection.variants.map((variant) => variant.artifactRevisionId)).size !==
      projection.variants.length
    ) {
      return "projection variants must have unique artifact revisions";
    }
    if (
      projection.variants.length > 0 &&
      !projection.variants.every(
        (variant) => variant.artifactId === projection.variants[0].artifactId,
      )
    ) {
      return "projection variants must share one artifact";
    }
    return (
      projection.variants.every((variant) => variant.aspectRatio === projection.aspectRatio) ||
      "projection variants must match the requested aspect ratio"
    );
  }),
);

export const ImageGenerationUiProjectionStandardSchema = Schema.toStandardSchemaV1(
  ImageGenerationUiProjectionSchema,
  {
    parseOptions: ImageGenerationBoundaryParseOptions,
  },
);

export const ProjectImageGenerationCommandStandardSchema = Schema.toStandardSchemaV1(
  ProjectImageGenerationCommandSchema,
  {
    parseOptions: ImageGenerationBoundaryParseOptions,
  },
);

const ImageRepositoryScopeFields = {
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  runId: BoundedIdentifierSchema,
  attemptId: BoundedIdentifierSchema,
  attemptGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
} as const;

export const PrepareProjectImageOperationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  batchOperationId: BoundedIdentifierSchema,
  responseMessageId: BoundedIdentifierSchema,
  requestedCount: ProjectImageVariantCountSchema,
  variantIndex: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(4)),
  threadId: BoundedIdentifierSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  toolCallId: BoundedIdentifierSchema,
  batchToolCallId: BoundedIdentifierSchema,
  provider: BoundedIdentifierSchema,
  model: BoundedIdentifierSchema,
  imageRequest: CreateProjectImageInputSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      request.variantIndex <= request.requestedCount ||
      "the image variant index must be within the requested batch",
  ),
);

export const ProjectImageResolvedObjectSchema = Schema.Struct({
  artifactId: BoundedIdentifierSchema,
  artifactRevisionId: BoundedIdentifierSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  name: ProjectFileNameSchema,
  contentType: Schema.Literals(["image/png", "image/jpeg", "image/webp"]),
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
});

const PreparedProjectImageOperationFields = {
  operationId: BoundedIdentifierSchema,
  artifactId: BoundedIdentifierSchema,
  artifactRevisionId: BoundedIdentifierSchema,
  storageObjectId: BoundedIdentifierSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  references: Schema.Array(ProjectImageResolvedObjectSchema).check(Schema.isMaxLength(14)),
} as const;

export const PrepareProjectImageOperationResultSchema = Schema.Union([
  Schema.Struct({
    ...PreparedProjectImageOperationFields,
    status: Schema.Literal("prepared"),
  }),
  Schema.Struct({
    ...PreparedProjectImageOperationFields,
    status: Schema.Literals(["generating", "saving", "failed", "cancelled", "outcome-unknown"]),
  }),
  Schema.Struct({
    ...PreparedProjectImageOperationFields,
    status: Schema.Literal("ready"),
    image: ProjectImageReferenceSchema,
  }),
]);

export const BeginProjectImageGenerationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  reservedProviderCostMicros: Schema.Literal(PROJECT_IMAGE_PROVIDER_COST_RESERVATION_MICROS),
});

export const ObserveProjectImageProviderGenerationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  providerGenerationId: BoundedIdentifierSchema,
});

export const SaveProjectImageGenerationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  inputTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  outputTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  totalTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  providerCostMicros: Schema.optionalKey(NonNegativeIntegerSchema),
  providerGenerationId: Schema.optionalKey(BoundedIdentifierSchema),
  warningCount: NonNegativeIntegerSchema,
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
});

export const FinalizeProjectImageOperationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  artifactRevisionId: BoundedIdentifierSchema,
  mediaType: Schema.Literal("image/png"),
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  etag: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512)),
  width: Schema.Int.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(3_200)),
  height: Schema.Int.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(3_200)),
});

export const FailProjectImageOperationRequestSchema = Schema.Struct({
  ...ImageRepositoryScopeFields,
  operationId: BoundedIdentifierSchema,
  status: Schema.Literals(["failed", "cancelled", "outcome-unknown"]),
  errorCode: ImageGenerationPublicErrorCodeSchema,
  inputTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  outputTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  totalTokens: Schema.optionalKey(NonNegativeIntegerSchema),
  providerCostMicros: Schema.optionalKey(NonNegativeIntegerSchema),
  providerGenerationId: Schema.optionalKey(BoundedIdentifierSchema),
}).check(
  Schema.makeFilter((request) => {
    if (request.status === "cancelled") {
      return (
        request.errorCode === "IMAGE_GENERATION_CANCELLED" ||
        "cancelled image operations require the cancellation error code"
      );
    }
    if (request.status === "outcome-unknown") {
      return (
        request.errorCode === "IMAGE_GENERATION_OUTCOME_UNKNOWN" ||
        "outcome-unknown image operations require the outcome-unknown error code"
      );
    }
    return (
      (request.errorCode !== "IMAGE_GENERATION_CANCELLED" &&
        request.errorCode !== "IMAGE_GENERATION_OUTCOME_UNKNOWN") ||
      "failed image operations cannot use cancellation or outcome-unknown error codes"
    );
  }),
);

export const ProjectImageOperationTransitionResultSchema = Schema.Struct({
  operationId: BoundedIdentifierSchema,
  status: ImageGenerationOperationStatusSchema,
});

export const ResolveProjectImageRevisionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: BoundedIdentifierSchema,
  artifactRevisionId: Schema.optionalKey(BoundedIdentifierSchema),
});

export const ResolvedProjectImageRevisionSchema = Schema.Struct({
  image: ProjectImageReferenceSchema,
  object: ProjectImageResolvedObjectSchema,
});

export const ProjectImageCleanupErrorCodeSchema = Schema.Literals([
  "WORKER_AUTHENTICATION_FAILED",
  "WORKER_INTEGRITY_ERROR",
  "WORKER_INVALID_RESPONSE",
  "WORKER_REQUEST_CANCELLED",
  "WORKER_UNAVAILABLE",
]);

export const ClaimProjectImageCleanupRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  owner: BoundedIdentifierSchema,
  limit: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_CLEANUP_BATCH_MAX),
  ),
});

export const ProjectImageCleanupClaimSchema = Schema.Struct({
  imageOperationId: BoundedIdentifierSchema,
  uploadId: UploadIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  objectKey: ProjectFilePrivateObjectKeySchema,
  contentType: Schema.Literal("image/png"),
  byteSize: ProjectFilePositiveIntegerSchema.check(
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_MAX_BYTES),
  ),
  sha256: ProjectFileSha256Schema,
  attempt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});
export const ProjectImageCleanupClaimsSchema = Schema.Array(ProjectImageCleanupClaimSchema).check(
  Schema.isMaxLength(PROJECT_IMAGE_CLEANUP_BATCH_MAX),
);

export const RecordProjectImageCleanupResultRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  imageOperationId: BoundedIdentifierSchema,
  owner: BoundedIdentifierSchema,
  attempt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  outcome: Schema.Literals(["confirmed", "retry"]),
  errorCode: Schema.optionalKey(ProjectImageCleanupErrorCodeSchema),
}).check(
  Schema.makeFilter((result) => (result.outcome === "retry") === (result.errorCode !== undefined), {
    expected: "only retry cleanup results include an error code",
  }),
);
export const ProjectImageCleanupResultSchema = Schema.Struct({
  imageOperationId: BoundedIdentifierSchema,
  status: Schema.Literals(["cleanup", "deleted", "cleanup-abandoned"]),
  attempts: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});

export const ProjectImageCostReconciliationErrorCodeSchema = Schema.Literal(
  "IMAGE_PROVIDER_ACCOUNTING_UNAVAILABLE",
);

export const ClaimProjectImageCostReconciliationRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  owner: BoundedIdentifierSchema,
  limit: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(PROJECT_IMAGE_COST_RECONCILIATION_BATCH_MAX),
  ),
});

export const ProjectImageCostReconciliationClaimSchema = Schema.Struct({
  imageOperationId: BoundedIdentifierSchema,
  providerGenerationId: BoundedIdentifierSchema,
  attempt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});

export const ProjectImageCostReconciliationClaimsSchema = Schema.Array(
  ProjectImageCostReconciliationClaimSchema,
).check(Schema.isMaxLength(PROJECT_IMAGE_COST_RECONCILIATION_BATCH_MAX));

export const RecordProjectImageCostReconciliationResultRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  imageOperationId: BoundedIdentifierSchema,
  owner: BoundedIdentifierSchema,
  attempt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  outcome: Schema.Literals(["reported", "retry"]),
  providerCostMicros: Schema.optionalKey(NonNegativeIntegerSchema),
  errorCode: Schema.optionalKey(ProjectImageCostReconciliationErrorCodeSchema),
}).check(
  Schema.makeFilter((result) => {
    if (result.outcome === "reported") {
      return (
        (result.providerCostMicros !== undefined && result.errorCode === undefined) ||
        "reported image cost reconciliation requires provider cost only"
      );
    }
    return (
      (result.providerCostMicros === undefined && result.errorCode !== undefined) ||
      "retry image cost reconciliation requires an error code only"
    );
  }),
);

export const ProjectImageCostReconciliationResultSchema = Schema.Struct({
  imageOperationId: BoundedIdentifierSchema,
  status: Schema.Literals(["reported", "unreported"]),
  attempts: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});

export const CreateProjectImageDownloadTicketRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: BoundedIdentifierSchema,
  artifactRevisionId: Schema.optionalKey(BoundedIdentifierSchema),
});

export const ProjectImageDownloadTicketSchema = Schema.Struct({
  image: ProjectImageReferenceSchema,
  url: ProjectFileHttpUrlSchema,
  expiresAt: ProjectFileIsoDateTimeSchema,
});

export const ProjectImageGenerationAvailabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
});

export const ProjectImageGenerationAvailabilitySchema = Schema.Struct({
  version: Schema.Literal("project-image-generation-availability/v1"),
  generationEnabled: Schema.Boolean,
});

export const ProbeProjectImageRuntimeRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  actorUserId: ProjectFileActorUserIdSchema,
});

export const ProjectImageRuntimeProbeSchema = Schema.Struct({
  version: Schema.Literal("project-image-runtime-probe/v1"),
});

export type ImageAspectRatio = typeof ImageAspectRatioSchema.Type;
export type ProjectImageVariantCount = typeof ProjectImageVariantCountSchema.Type;
export type CreateProjectImageInput = typeof CreateProjectImageInputSchema.Type;
export type CreateProjectImageInputEncoded = typeof CreateProjectImageInputSchema.Encoded;
export type CreateProjectImagesInput = typeof CreateProjectImagesInputSchema.Type;
export type CreateProjectImagesInputEncoded = typeof CreateProjectImagesInputSchema.Encoded;
export type ProjectImageGenerationCommand = typeof ProjectImageGenerationCommandSchema.Type;
export type ProjectImageReference = typeof ProjectImageReferenceSchema.Type;
export type ProjectImageReferenceEncoded = typeof ProjectImageReferenceSchema.Encoded;
export type ProjectImageBatchResult = typeof ProjectImageBatchResultSchema.Type;
export type ImageGenerationOperationStatus = typeof ImageGenerationOperationStatusSchema.Type;
export type ImageGenerationPublicErrorCode = typeof ImageGenerationPublicErrorCodeSchema.Type;
export type ImageGenerationUiProjection = typeof ImageGenerationUiProjectionSchema.Type;
export type PrepareProjectImageOperationRequest =
  typeof PrepareProjectImageOperationRequestSchema.Type;
export type PrepareProjectImageOperationResult =
  typeof PrepareProjectImageOperationResultSchema.Type;
export type ProjectImageResolvedObject = typeof ProjectImageResolvedObjectSchema.Type;
export type BeginProjectImageGenerationRequest =
  typeof BeginProjectImageGenerationRequestSchema.Type;
export type ObserveProjectImageProviderGenerationRequest =
  typeof ObserveProjectImageProviderGenerationRequestSchema.Type;
export type SaveProjectImageGenerationRequest = typeof SaveProjectImageGenerationRequestSchema.Type;
export type FinalizeProjectImageOperationRequest =
  typeof FinalizeProjectImageOperationRequestSchema.Type;
export type FailProjectImageOperationRequest = typeof FailProjectImageOperationRequestSchema.Type;
export type ProjectImageOperationTransitionResult =
  typeof ProjectImageOperationTransitionResultSchema.Type;
export type ResolveProjectImageRevisionRequest =
  typeof ResolveProjectImageRevisionRequestSchema.Type;
export type ResolvedProjectImageRevision = typeof ResolvedProjectImageRevisionSchema.Type;
export type ClaimProjectImageCleanupRequest = typeof ClaimProjectImageCleanupRequestSchema.Type;
export type ProjectImageCleanupClaim = typeof ProjectImageCleanupClaimSchema.Type;
export type ProjectImageCleanupErrorCode = typeof ProjectImageCleanupErrorCodeSchema.Type;
export type RecordProjectImageCleanupResultRequest =
  typeof RecordProjectImageCleanupResultRequestSchema.Type;
export type ProjectImageCleanupResult = typeof ProjectImageCleanupResultSchema.Type;
export type ClaimProjectImageCostReconciliationRequest =
  typeof ClaimProjectImageCostReconciliationRequestSchema.Type;
export type ProjectImageCostReconciliationClaim =
  typeof ProjectImageCostReconciliationClaimSchema.Type;
export type ProjectImageCostReconciliationErrorCode =
  typeof ProjectImageCostReconciliationErrorCodeSchema.Type;
export type RecordProjectImageCostReconciliationResultRequest =
  typeof RecordProjectImageCostReconciliationResultRequestSchema.Type;
export type ProjectImageCostReconciliationResult =
  typeof ProjectImageCostReconciliationResultSchema.Type;
export type CreateProjectImageDownloadTicketRequest =
  typeof CreateProjectImageDownloadTicketRequestSchema.Type;
export type ProjectImageDownloadTicket = typeof ProjectImageDownloadTicketSchema.Type;
export type ProjectImageGenerationAvailabilityRequest =
  typeof ProjectImageGenerationAvailabilityRequestSchema.Type;
export type ProjectImageGenerationAvailability =
  typeof ProjectImageGenerationAvailabilitySchema.Type;
export type ProbeProjectImageRuntimeRequest = typeof ProbeProjectImageRuntimeRequestSchema.Type;
export type ProjectImageRuntimeProbe = typeof ProjectImageRuntimeProbeSchema.Type;

export const encodeProjectImageReference = Schema.encodeSync(ProjectImageReferenceSchema);
export const encodeProjectImageBatchResult = Schema.encodeSync(ProjectImageBatchResultSchema);
export const decodeCreateProjectImagesInput = Schema.decodeUnknownSync(
  CreateProjectImagesInputSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageGenerationCommand = Schema.decodeUnknownSync(
  ProjectImageGenerationCommandSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageBatchResultOption = Schema.decodeUnknownOption(
  ProjectImageBatchResultSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageReference = Schema.decodeUnknownSync(
  ProjectImageReferenceSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeImageGenerationUiProjection = Schema.decodeUnknownSync(
  ImageGenerationUiProjectionSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeImageGenerationUiProjection = Schema.encodeSync(
  ImageGenerationUiProjectionSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodePrepareProjectImageOperationRequest = Schema.decodeUnknownSync(
  PrepareProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodePrepareProjectImageOperationRequest = Schema.encodeSync(
  PrepareProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodePrepareProjectImageOperationResult = Schema.decodeUnknownSync(
  PrepareProjectImageOperationResultSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeBeginProjectImageGenerationRequest = Schema.decodeUnknownSync(
  BeginProjectImageGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeBeginProjectImageGenerationRequest = Schema.encodeSync(
  BeginProjectImageGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeObserveProjectImageProviderGenerationRequest = Schema.decodeUnknownSync(
  ObserveProjectImageProviderGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeObserveProjectImageProviderGenerationRequest = Schema.encodeSync(
  ObserveProjectImageProviderGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeSaveProjectImageGenerationRequest = Schema.decodeUnknownSync(
  SaveProjectImageGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeSaveProjectImageGenerationRequest = Schema.encodeSync(
  SaveProjectImageGenerationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeFinalizeProjectImageOperationRequest = Schema.decodeUnknownSync(
  FinalizeProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeFinalizeProjectImageOperationRequest = Schema.encodeSync(
  FinalizeProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeFailProjectImageOperationRequest = Schema.decodeUnknownSync(
  FailProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeFailProjectImageOperationRequest = Schema.encodeSync(
  FailProjectImageOperationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageOperationTransitionResult = Schema.decodeUnknownSync(
  ProjectImageOperationTransitionResultSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeResolveProjectImageRevisionRequest = Schema.decodeUnknownSync(
  ResolveProjectImageRevisionRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeResolveProjectImageRevisionRequest = Schema.encodeSync(
  ResolveProjectImageRevisionRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeResolvedProjectImageRevision = Schema.decodeUnknownSync(
  ResolvedProjectImageRevisionSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeClaimProjectImageCleanupRequest = Schema.decodeUnknownSync(
  ClaimProjectImageCleanupRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeClaimProjectImageCleanupRequest = Schema.encodeSync(
  ClaimProjectImageCleanupRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageCleanupClaims = Schema.decodeUnknownSync(
  ProjectImageCleanupClaimsSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeRecordProjectImageCleanupResultRequest = Schema.decodeUnknownSync(
  RecordProjectImageCleanupResultRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeRecordProjectImageCleanupResultRequest = Schema.encodeSync(
  RecordProjectImageCleanupResultRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageCleanupResult = Schema.decodeUnknownSync(
  ProjectImageCleanupResultSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeClaimProjectImageCostReconciliationRequest = Schema.decodeUnknownSync(
  ClaimProjectImageCostReconciliationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeClaimProjectImageCostReconciliationRequest = Schema.encodeSync(
  ClaimProjectImageCostReconciliationRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageCostReconciliationClaims = Schema.decodeUnknownSync(
  ProjectImageCostReconciliationClaimsSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeRecordProjectImageCostReconciliationResultRequest = Schema.decodeUnknownSync(
  RecordProjectImageCostReconciliationResultRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeRecordProjectImageCostReconciliationResultRequest = Schema.encodeSync(
  RecordProjectImageCostReconciliationResultRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageCostReconciliationResult = Schema.decodeUnknownSync(
  ProjectImageCostReconciliationResultSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeCreateProjectImageDownloadTicketRequest = Schema.decodeUnknownSync(
  CreateProjectImageDownloadTicketRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageDownloadTicket = Schema.decodeUnknownSync(
  ProjectImageDownloadTicketSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageGenerationAvailabilityRequest = Schema.decodeUnknownSync(
  ProjectImageGenerationAvailabilityRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageGenerationAvailability = Schema.decodeUnknownSync(
  ProjectImageGenerationAvailabilitySchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProbeProjectImageRuntimeRequest = Schema.decodeUnknownSync(
  ProbeProjectImageRuntimeRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const encodeProbeProjectImageRuntimeRequest = Schema.encodeSync(
  ProbeProjectImageRuntimeRequestSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeProjectImageRuntimeProbe = Schema.decodeUnknownSync(
  ProjectImageRuntimeProbeSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeImageGenerationPublicError = Schema.decodeUnknownSync(
  ImageGenerationPublicErrorSchema,
  ImageGenerationBoundaryParseOptions,
);
export const decodeImageGenerationRepositoryErrorData = Schema.decodeUnknownSync(
  ImageGenerationRepositoryErrorDataSchema,
  ImageGenerationBoundaryParseOptions,
);
